import { Zap, Activity, Power } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useDeviceStats } from '../hooks/useDeviceStats';
import { toast } from 'sonner';

export function BoostDashboard() {
  const stats = useDeviceStats();
  const [isActive, setIsActive] = useState(false);
  const [boostFps, setBoostFps] = useState(stats.fps);

  useEffect(() => {
    setBoostFps(stats.fps);
  }, [stats.fps]);

  const handleBoostToggle = () => {
    if (!isActive) {
      setIsActive(true);
      toast.success('FPS Boost activated! Optimizing performance...', {
        description: 'Device optimization in progress',
      });
      // Simulate boost - in real app this would trigger actual optimization
      const boostInterval = setInterval(() => {
        setBoostFps(prev => {
          if (prev >= 120) {
            clearInterval(boostInterval);
            return 120;
          }
          return Math.min(120, prev + 10);
        });
      }, 200);
    } else {
      setIsActive(false);
      setBoostFps(stats.fps);
      toast.info('FPS Boost deactivated', {
        description: 'Returning to normal performance',
      });
    }
  };

  const displayFps = isActive ? boostFps : Math.round(stats.fps);
  const fpsPercentage = (displayFps / 120) * 100;

  const getStatusColor = () => {
    if (!isActive) return 'text-muted-foreground';
    if (displayFps < 80) return 'text-yellow-400';
    if (displayFps < 100) return 'text-primary';
    return 'text-accent';
  };
  return (
    <div className="space-y-8">
      {/* Main FPS Display */}
      <Card className="p-8 text-center backdrop-blur-sm border-primary/30 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 pointer-events-none" />
        <div className="relative space-y-4">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Current FPS</p>
          <div className={`text-6xl font-bold transition-colors duration-300 ${getStatusColor()}`}>
            {displayFps}
            <span className="text-2xl text-muted-foreground ml-2">fps</span>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 space-y-2">
            <div className="h-3 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent via-primary to-accent transition-all duration-300"
                style={{ width: `${fpsPercentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>60 fps</span>
              <span>120 fps</span>
            </div>
          </div>

          {/* Status Indicator */}
          <div className="flex items-center justify-center gap-2 pt-2">
            {isActive ? (
              <div className="flex items-center gap-1.5 text-accent">
                <Activity size={16} className="animate-pulse" />
                <span className="text-sm font-medium">Boosting</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Ready to boost</p>
            )}
          </div>
        </div>
      </Card>

      {/* Control Buttons */}
      <div className="flex gap-4">
        <Button
          size="lg"
          onClick={handleBoostToggle}
          className={`flex-1 h-16 text-lg font-semibold gap-2 transition-all duration-300 ${
            isActive
              ? 'bg-destructive hover:bg-destructive/90'
              : 'bg-primary hover:bg-primary/90 text-primary-foreground'
          }`}
        >
          {isActive ? (
            <>
              <Power size={24} />
              Stop Boost
            </>
          ) : (
            <>
              <Zap size={24} />
              Start Boost
            </>
          )}
        </Button>
      </div>

      {/* Info Cards */}
      <div className="space-y-3">
        <Card className="p-4 backdrop-blur-sm border-primary/30">
          <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
            <Zap size={18} className="text-accent" />
            Peak Performance
          </h3>
          <p className="text-sm text-muted-foreground">
            Boost your FPS up to 120fps with advanced optimization.
          </p>
        </Card>
        <Card className="p-4 backdrop-blur-sm border-primary/30">
          <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
            <Activity size={18} className="text-primary" />
            Device Status
          </h3>
          <p className="text-sm text-muted-foreground">
            Battery: {stats.battery}% | Ping: {stats.ping}ms
          </p>
        </Card>
      </div>
    </div>
  );
}